/**
 * Transaction process graph for event-kit-rental-v2
 *
 * RTE's simplified V1-flow rental process:
 * - 24h acceptance window
 * - Host-initiated pickup handshake (no dual confirm, no 4-digit code)
 * - Host is the decider on return (no renter-submit step)
 * - Pickup-issue cancel with full refund
 * - Dispute resolution
 * - Sliding cancellation refund pre-handoff
 * - 14-day review window
 *
 * Differs from v1 in pickup/return mechanics. See
 * docs/design/event-kit-rental-v2-handoff.md.
 */

// ============================================================
// TRANSITIONS
// ============================================================

export const transitions = {
  // Pre-booking
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Payment
  EXPIRE_PAYMENT: 'transition/expire-payment',
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // Accept / Decline / Expire
  ACCEPT: 'transition/accept',
  OPERATOR_ACCEPT: 'transition/operator-accept',
  DECLINE: 'transition/decline',
  OPERATOR_DECLINE: 'transition/operator-decline',
  EXPIRE: 'transition/expire',

  // Cancellations (pre-handoff)
  CUSTOMER_CANCEL_PRE_ACCEPT: 'transition/customer-cancel-pre-accept',
  CUSTOMER_CANCEL_POST_ACCEPT: 'transition/customer-cancel-post-accept',
  CUSTOMER_CANCEL_POST_ACCEPT_PARTIAL: 'transition/customer-cancel-post-accept-partial',
  PROVIDER_CANCEL: 'transition/provider-cancel',
  OPERATOR_CANCEL_ACCEPTED: 'transition/operator-cancel-accepted',

  // Reminder self-loops
  SEND_PICKUP_REMINDER: 'transition/send-pickup-reminder',
  SEND_RETURN_REMINDER: 'transition/send-return-reminder',

  // Pickup handshake (host-initiated)
  PROVIDER_MARK_KIT_READY: 'transition/provider-mark-kit-ready',
  PROVIDER_UNMARK_KIT_READY: 'transition/provider-unmark-kit-ready',
  CUSTOMER_START_RENTAL: 'transition/customer-start-rental',
  CUSTOMER_START_RENTAL_WITH_ISSUE: 'transition/customer-start-rental-with-issue',

  // Pickup-issue cancel (LEGACY instant full refund, pre-v10; the UI no
  // longer fires these — kept for old-transaction history rendering).
  // Two source-state variants since Sharetribe transitions take a single :from.
  CUSTOMER_CANCEL_AT_PICKUP_ISSUE: 'transition/customer-cancel-at-pickup-issue',
  CUSTOMER_CANCEL_AT_PICKUP_ISSUE_PRE_READY:
    'transition/customer-cancel-at-pickup-issue-pre-ready',

  // Pickup-issue refund review (process v10, owner-directed 2026-07-08):
  // the renter's cancel choice parks the tx for operator review; money
  // moves only on resolution. See docs/design/pickup-issue-refund-review.md.
  CUSTOMER_REQUEST_PICKUP_REFUND: 'transition/customer-request-pickup-refund',
  CUSTOMER_REQUEST_PICKUP_REFUND_PRE_READY:
    'transition/customer-request-pickup-refund-pre-ready',
  OPERATOR_APPROVE_PICKUP_REFUND: 'transition/operator-approve-pickup-refund',
  OPERATOR_DENY_PICKUP_REFUND: 'transition/operator-deny-pickup-refund',
  OPERATOR_RESUME_PICKUP_KIT_READY: 'transition/operator-resume-pickup-kit-ready',
  OPERATOR_RESUME_PICKUP_ACCEPTED: 'transition/operator-resume-pickup-accepted',
  AUTO_APPROVE_PICKUP_REFUND: 'transition/auto-approve-pickup-refund',

  // Return handoff (host is decider)
  PROVIDER_ACCEPT_RETURN: 'transition/provider-accept-return',
  PROVIDER_REPORT_DAMAGE: 'transition/provider-report-damage',

  // Operator overrides
  OPERATOR_MARK_KIT_READY: 'transition/operator-mark-kit-ready',
  OPERATOR_UNMARK_KIT_READY: 'transition/operator-unmark-kit-ready',
  OPERATOR_FORCE_START_RENTAL: 'transition/operator-force-start-rental',
  OPERATOR_FORCE_ACCEPT_RETURN: 'transition/operator-force-accept-return',
  OPERATOR_REPORT_DAMAGE: 'transition/operator-report-damage',
  OPERATOR_CANCEL_KIT_READY: 'transition/operator-cancel-kit-ready',
  OPERATOR_CANCEL_ACTIVE_RENTAL: 'transition/operator-cancel-active-rental',

  // Dispute
  OPERATOR_RESOLVE_PARTIAL: 'transition/operator-resolve-partial',
  OPERATOR_RESOLVE_FULL: 'transition/operator-resolve-full',

  // Reviews
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
};

// ============================================================
// STATES
// ============================================================

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  CANCELLED_BY_CUSTOMER: 'cancelled-by-customer',
  CANCELLED_BY_PROVIDER: 'cancelled-by-provider',
  CANCELLED_BY_CUSTOMER_PICKUP_ISSUE: 'cancelled-by-customer-pickup-issue',

  // Pickup-issue refund review (v10): held state + denied terminal.
  PICKUP_ISSUE_REVIEW: 'pickup-issue-review',
  CANCELLED_PICKUP_REFUND_DENIED: 'cancelled-pickup-refund-denied',

  // Handshake + active rental
  KIT_READY: 'kit-ready',
  ACTIVE_RENTAL: 'active-rental',

  // Completion + dispute
  COMPLETED: 'completed',
  DISPUTED: 'disputed',
  RESOLVED_PARTIAL: 'resolved-partial',
  RESOLVED_FULL: 'resolved-full',

  // Reviews
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

// ============================================================
// GRAPH
// ============================================================

export const graph = {
  id: 'event-kit-rental-v2/release-1',
  initial: states.INITIAL,
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },
    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },
    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.CUSTOMER_CANCEL_PRE_ACCEPT]: states.CANCELLED_BY_CUSTOMER,
      },
    },
    [states.ACCEPTED]: {
      on: {
        [transitions.CUSTOMER_CANCEL_POST_ACCEPT]: states.CANCELLED_BY_CUSTOMER,
        [transitions.CUSTOMER_CANCEL_POST_ACCEPT_PARTIAL]: states.CANCELLED_BY_CUSTOMER,
        [transitions.PROVIDER_CANCEL]: states.CANCELLED_BY_PROVIDER,
        [transitions.OPERATOR_CANCEL_ACCEPTED]: states.CANCELLED_BY_PROVIDER,
        [transitions.SEND_PICKUP_REMINDER]: states.ACCEPTED,
        [transitions.PROVIDER_MARK_KIT_READY]: states.KIT_READY,
        [transitions.OPERATOR_MARK_KIT_READY]: states.KIT_READY,
        [transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE_PRE_READY]:
          states.CANCELLED_BY_CUSTOMER_PICKUP_ISSUE,
        [transitions.CUSTOMER_REQUEST_PICKUP_REFUND_PRE_READY]:
          states.PICKUP_ISSUE_REVIEW,
      },
    },
    [states.KIT_READY]: {
      on: {
        [transitions.PROVIDER_UNMARK_KIT_READY]: states.ACCEPTED,
        [transitions.OPERATOR_UNMARK_KIT_READY]: states.ACCEPTED,
        [transitions.CUSTOMER_START_RENTAL]: states.ACTIVE_RENTAL,
        [transitions.CUSTOMER_START_RENTAL_WITH_ISSUE]: states.ACTIVE_RENTAL,
        [transitions.OPERATOR_FORCE_START_RENTAL]: states.ACTIVE_RENTAL,
        [transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE]:
          states.CANCELLED_BY_CUSTOMER_PICKUP_ISSUE,
        [transitions.CUSTOMER_REQUEST_PICKUP_REFUND]: states.PICKUP_ISSUE_REVIEW,
        [transitions.OPERATOR_CANCEL_KIT_READY]: states.CANCELLED_BY_PROVIDER,
      },
    },
    [states.PICKUP_ISSUE_REVIEW]: {
      on: {
        [transitions.OPERATOR_APPROVE_PICKUP_REFUND]:
          states.CANCELLED_BY_CUSTOMER_PICKUP_ISSUE,
        [transitions.AUTO_APPROVE_PICKUP_REFUND]:
          states.CANCELLED_BY_CUSTOMER_PICKUP_ISSUE,
        [transitions.OPERATOR_DENY_PICKUP_REFUND]:
          states.CANCELLED_PICKUP_REFUND_DENIED,
        [transitions.OPERATOR_RESUME_PICKUP_KIT_READY]: states.KIT_READY,
        [transitions.OPERATOR_RESUME_PICKUP_ACCEPTED]: states.ACCEPTED,
      },
    },
    [states.CANCELLED_PICKUP_REFUND_DENIED]: {},
    [states.ACTIVE_RENTAL]: {
      on: {
        [transitions.SEND_RETURN_REMINDER]: states.ACTIVE_RENTAL,
        [transitions.PROVIDER_ACCEPT_RETURN]: states.COMPLETED,
        [transitions.PROVIDER_REPORT_DAMAGE]: states.DISPUTED,
        [transitions.OPERATOR_FORCE_ACCEPT_RETURN]: states.COMPLETED,
        [transitions.OPERATOR_REPORT_DAMAGE]: states.DISPUTED,
        [transitions.OPERATOR_CANCEL_ACTIVE_RENTAL]: states.CANCELLED_BY_PROVIDER,
      },
    },
    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.CANCELLED_BY_CUSTOMER]: {},
    [states.CANCELLED_BY_PROVIDER]: {},
    [states.CANCELLED_BY_CUSTOMER_PICKUP_ISSUE]: {},

    // Dispute resolution
    [states.DISPUTED]: {
      on: {
        [transitions.OPERATOR_RESOLVE_PARTIAL]: states.RESOLVED_PARTIAL,
        [transitions.OPERATOR_RESOLVE_FULL]: states.RESOLVED_FULL,
      },
    },
    [states.RESOLVED_PARTIAL]: {},
    [states.RESOLVED_FULL]: {},

    // Completion + Reviews
    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.CUSTOMER_CANCEL_PRE_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT_PARTIAL,
    transitions.PROVIDER_CANCEL,
    transitions.PROVIDER_MARK_KIT_READY,
    transitions.PROVIDER_UNMARK_KIT_READY,
    transitions.CUSTOMER_START_RENTAL,
    transitions.CUSTOMER_START_RENTAL_WITH_ISSUE,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE_PRE_READY,
    transitions.CUSTOMER_REQUEST_PICKUP_REFUND,
    transitions.CUSTOMER_REQUEST_PICKUP_REFUND_PRE_READY,
    transitions.OPERATOR_APPROVE_PICKUP_REFUND,
    transitions.OPERATOR_DENY_PICKUP_REFUND,
    transitions.OPERATOR_RESUME_PICKUP_KIT_READY,
    transitions.OPERATOR_RESUME_PICKUP_ACCEPTED,
    transitions.AUTO_APPROVE_PICKUP_REFUND,
    transitions.PROVIDER_ACCEPT_RETURN,
    transitions.PROVIDER_REPORT_DAMAGE,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_FULL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT_PARTIAL,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE_PRE_READY,
    transitions.OPERATOR_APPROVE_PICKUP_REFUND,
    transitions.OPERATOR_DENY_PICKUP_REFUND,
    transitions.OPERATOR_CANCEL_KIT_READY,
    transitions.OPERATOR_CANCEL_ACTIVE_RENTAL,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_FULL,
  ].includes(transition);
};

export const isCompleted = transition => {
  return [
    transitions.PROVIDER_ACCEPT_RETURN,
    transitions.OPERATOR_FORCE_ACCEPT_RETURN,
    transitions.OPERATOR_RESOLVE_PARTIAL,
    transitions.OPERATOR_RESOLVE_FULL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ].includes(transition);
};

export const isRefunded = transition => {
  return [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.CUSTOMER_CANCEL_PRE_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT,
    transitions.CUSTOMER_CANCEL_POST_ACCEPT_PARTIAL,
    transitions.PROVIDER_CANCEL,
    transitions.OPERATOR_CANCEL_ACCEPTED,
    transitions.OPERATOR_CANCEL_KIT_READY,
    transitions.OPERATOR_CANCEL_ACTIVE_RENTAL,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE,
    transitions.CUSTOMER_CANCEL_AT_PICKUP_ISSUE_PRE_READY,
    transitions.OPERATOR_APPROVE_PICKUP_REFUND,
    transitions.AUTO_APPROVE_PICKUP_REFUND,
    transitions.OPERATOR_DENY_PICKUP_REFUND,
  ].includes(transition);
};

// Only flag states where the user actually needs to take action right now.
// Time-conditional attention (e.g. host on pickup day with state=accepted)
// is handled in the UI, not here, so the badge doesn't fire for every
// upcoming booking.
export const statesNeedingProviderAttention = [states.PREAUTHORIZED];

export const statesNeedingCustomerAttention = [states.KIT_READY];
